import React from "react";
import Logo from "../assets/new logo.svg";

export default function StatsCard({
  answeredToday = 54,
  // new explicit props, keep backward-compatible fallbacks
  todayPercent,
  last7Percent,
  last30Percent,
  percentCorrect, // fallback
  recentScore, // fallback
  onStartQuiz = () => {},
  onAddQuestions = () => {}
}) {
  const clamp = (v) => Math.max(0, Math.min(100, Number(v) || 0));

  // resolve percentages (explicit props take precedence)
  const tp = clamp(todayPercent ?? percentCorrect ?? 0);
  const p7 = clamp(last7Percent ?? recentScore ?? 0);
  const p30 = clamp(last30Percent ?? 82);

  // spacing multiplier 1.5x (base values) then scale all borders by 0.9
  const spacingScale = 0.9;
  const pad = 28 * 1.5 * spacingScale; // was 42 -> now 37.8
  const decoOffset = 18 * 1.5 * spacingScale; // was 27 -> now 24.3
  const innerLeft = 28 * 1.5 * spacingScale; // was 42 -> now 37.8
  const innerBottom = 28 * 1.5 * spacingScale; // was 42 -> now 37.8
  const innerRight = 28 * 1.5 * spacingScale; // was 42 -> now 37.8
  const cardTop = 24 * 1.5 * spacingScale; // was 36 -> now 32.4

  // move the answered block further left so the number sits ~20px farther from the card right edge
  const answeredRight = innerRight + 20;

  return (
    <div
      style={{
        position: "absolute",
        top: cardTop - 20, // reduce top spacing
        left: "50%",
        transform: "translateX(-50%)",
        width: 400, // increase width for better visibility
        height: 280, // increase height for better visibility
        boxSizing: "border-box",
        background: "#191919",
        borderRadius: 49,
        overflow: "hidden",
        padding: pad - 5, // reduce padding
        color: "#fff",
        fontFamily: "'Inter', 'InterVariable', Arial, sans-serif",
        zIndex: 20
      }}
    >
      {/* Card title 'Stats' on the left */}
      <div
        style={{
          position: "absolute",
          top: decoOffset + 8, // moved 10px down
          left: decoOffset + 10, // moved 10px right
          fontFamily: "Inter",
          fontStyle: "normal",
          fontWeight: 600,
          fontSize: 22, // increased font size
          color: "#FFFFFF"
        }}
      >
        Stats
      </div>

      {/* Answered Today in top-right: two stacked right-aligned words with 20px gap to the number */}
      <div
        style={{
          position: "absolute",
          // move right 20px and up 10px
          right: answeredRight - 20,
          top: pad - 10,
          height: 39,
          display: "flex",
          alignItems: "center",
          gap: 10,
          boxSizing: "border-box"
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", lineHeight: "15px", alignItems: "flex-end", textAlign: "right" }}>
          <span style={{
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: 12,
            color: "#FFFFFF",
            display: "block",
            lineHeight: '16px'
          }}>Answered</span>
          <span style={{
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: 12,
            color: "#FFFFFF",
            display: "block",
            lineHeight: '16px'
          }}>Today</span>
        </div>

        <div style={{
          fontFamily: "Inter",
          fontStyle: "normal",
          fontWeight: 500,
          fontSize: 32,
          lineHeight: "39px",
          color: "#FFFFFF",
          textAlign: "right"
        }}>
          {answeredToday}
        </div>
      </div>

      {/* Lower-left: progress bars group with increased inner spacing */}
      <div style={{
        position: "absolute",
        // move down 10px and right 10px
        left: innerLeft + 0,
        bottom: innerBottom - 10,
        width: 180,
        height: 150,
        display: "flex",
        alignItems: "flex-end",
        gap: 25,
        paddingTop: 8
      }}>
        {/* Today column */}
        <div style={{ width: 25, height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, lineHeight: "15px", color: "#fff", marginBottom: 6 }}>{tp}%</div>
          <div style={{ width: 17, height: 100, background: "#2A2A2A", borderRadius: 8.5, display: "flex", alignItems: "flex-end", overflow: "hidden" }}>
            <div style={{ width: "100%", height: `${tp}%`, background: "#515151", borderRadius: 8.5 }} />
          </div>
          <div style={{ marginTop: 8, fontSize: 12, lineHeight: "15px", color: "#fff" }}>T</div>
        </div>

        {/* Last 7 days column */}
        <div style={{ width: 25, height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, lineHeight: "15px", color: "#fff", marginBottom: 6 }}>{p7}%</div>
          <div style={{ width: 17, height: 100, background: "#2A2A2A", borderRadius: 8.5, display: "flex", alignItems: "flex-end", overflow: "hidden" }}>
            <div style={{ width: "100%", height: `${p7}%`, background: "#515151", borderRadius: 8.5 }} />
          </div>
          <div style={{ marginTop: 8, fontSize: 12, lineHeight: "15px", color: "#fff" }}>7</div>
        </div>

        {/* Last 30 days column */}
        <div style={{ width: 25, height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, lineHeight: "15px", color: "#fff", marginBottom: 6 }}>{p30}%</div>
          <div style={{ width: 17, height: 100, background: "#2A2A2A", borderRadius: 8.5, display: "flex", alignItems: "flex-end", overflow: "hidden" }}>
            <div style={{ width: "100%", height: `${p30}%`, background: "#515151", borderRadius: 8.5 }} />
          </div>
          <div style={{ marginTop: 8, fontSize: 12, lineHeight: "15px", color: "#fff" }}>30</div>
        </div>
      </div>

      {/* Lower-right: two stacked buttons with increased spacing from borders */}
      <div style={{
        position: "absolute",
        right: innerRight,
        bottom: innerBottom,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        alignItems: "flex-end"
      }}>
        <button
          onClick={onStartQuiz}
          style={{
            width: 135,
            height: 48,
            background: "#1E83F6",
            borderRadius: 19,
            border: "none",
            color: "#fff", // Changed font color to white
            fontFamily: "Inter",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer"
          }}
        >
          Start Quiz
        </button>

        <button
          onClick={onAddQuestions}
          style={{
            width: 135,
            height: 48,
            background: "#111111",
            borderRadius: 19,
            border: "1px solid rgba(30, 131, 246, 0.25)",
            color: "#1E83F6",
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: 14,
            cursor: "pointer"
          }}
        >
          Add Questions
        </button>
      </div>
    </div>
  );
}